import { FC, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Dropdown, Menu } from 'antd';
import type { AxiosResponse } from 'axios';
import { TbBraces, TbDownload, TbFileSpreadsheet, TbFileTypeCsv } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';

import type { VantageExportFormat } from '../../../../../types';

interface VantageExportDropdownProps {
    disabled?: boolean;
    sql: string;
}

const EXPORT_OPTIONS: Record<
    VantageExportFormat,
    { description: string; extension: string; icon: JSX.Element; label: string }
> = {
    csv: {
        description: 'Comma-separated values for spreadsheet and warehouse workflows.',
        extension: 'csv',
        icon: <TbFileTypeCsv size='1.125rem' />,
        label: 'CSV',
    },
    xlsx: {
        description: 'Excel workbook containing the complete governed query result.',
        extension: 'xlsx',
        icon: <TbFileSpreadsheet size='1.125rem' />,
        label: 'Excel Workbook',
    },
    json: {
        description: 'Structured result rows and execution metadata for programmatic use.',
        extension: 'json',
        icon: <TbBraces size='1.125rem' />,
        label: 'JSON',
    },
};

const getFilename = (response: AxiosResponse, format: VantageExportFormat) => {
    const disposition = response.headers?.['content-disposition'] ?? '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);

    return filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : `vantage-query.${EXPORT_OPTIONS[format].extension}`;
};

const VantageExportDropdown: FC<VantageExportDropdownProps> = ({ disabled, sql }) => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(false);

    const exportResult = async (format: VantageExportFormat) => {
        const option = EXPORT_OPTIONS[format];
        const toastId = toast.loading(`Preparing ${option.label} Export`, {
            description: 'Running the governed query without the 1,000-row viewing cap.',
            icon: <LoadingOutlined spin />,
        });

        setLoading(true);

        try {
            const response = await authorizedAxiosInstance.post(
                '/api/vantage-queries/export',
                { format, sql },
                { responseType: 'blob' },
            );
            const contentType = response.headers?.['content-type'];
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: typeof contentType === 'string' ? contentType : undefined }),
            );
            const link = document.createElement('a');

            link.href = url;
            link.download = getFilename(response, format);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`${option.label} Download Started`, {
                id: toastId,
                description: 'The complete export should appear in your downloads shortly.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Vantage Export Failed', {
                id: toastId,
                description: 'Confirm the query is executable and try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dropdown
            disabled={disabled || loading}
            placement='topRight'
            trigger={['click']}
            popupRender={() => (
                <DropdownMenuPanel
                    onClick={({ key }) => exportResult(key as VantageExportFormat)}
                    style={{ width: 19 * 16 }}
                >
                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>Export Query Results</small>
                    <Menu.Divider />
                    {(
                        Object.entries(EXPORT_OPTIONS) as Array<
                            [VantageExportFormat, (typeof EXPORT_OPTIONS)[VantageExportFormat]]
                        >
                    ).map(([format, option]) => (
                        <Menu.Item key={format}>
                            <DropdownOptionContent
                                avatar={{
                                    icon: option.icon,
                                    props: { size: 28 },
                                    style: { background: 'transparent', border: 'none' },
                                }}
                                description={option.description}
                                title={option.label}
                            />
                        </Menu.Item>
                    ))}
                </DropdownMenuPanel>
            )}
        >
            <DropdownButton size='small' loading={loading} disabled={disabled}>
                <TbDownload />
                Export
            </DropdownButton>
        </Dropdown>
    );
};

export default VantageExportDropdown;
